/**
 * One answer to "may this user open this surface".
 *
 * <h3>Why this file exists</h3>
 *
 * The left navigation used to be driven by ROLE_VISIBLE_IDS — a hand-written
 * map of role name to the nav ids that role may see, living in
 * DashboardLayoutV2. It was a second copy of the RBAC matrix, kept in the
 * frontend, keyed by a string:
 *
 *   - A role absent from the map resolved to `[]`, and the section filter then
 *     removed every item, so the whole rail rendered empty with no error. That
 *     is how the Protect role shipped with no navigation at all.
 *   - A CUSTOM role could never appear in it, because custom roles are rows in
 *     a table, not literals in a source file. Every custom role got the same
 *     blank rail, by construction.
 *   - It drifted from the route guards it was supposed to mirror. The old map
 *     omitted `multi-tenancy` for roles that hold `instance.read`, and listed
 *     ids whose routes demand rights the role does not hold — a visible link
 *     that answers 403.
 *
 * Access is already decided, server-side, per user: AuthService embeds the
 * caller's effective permission set as `session.permissions` (built from the
 * DB-backed matrix, which includes custom roles), and AuthContext.hasPermission
 * reads it. The navigation now asks that same question instead of looking the
 * user's role up in a list.
 *
 * <h3>The gates are the route's own gates</h3>
 *
 * Each entry below is the guard its route carries in App.jsx, so a surface
 * cannot appear in the rail and then refuse when opened, or be reachable by URL
 * and missing from the rail. `routeGates.test.js` parses App.jsx and asserts
 * this map still equals the inline guards, so the two cannot drift apart.
 */
import { roleLevel, gateLevel } from './roleLevels';

/**
 * Decide one gate. Pure: the caller supplies the identity.
 *
 * Mirrors RoleGuard's rules exactly — RoleGuard calls this for the allow
 * decision and keeps its own per-gate flags only to word the refusal.
 *
 * @param {object|null|undefined} gate  {requiredPermission, anyPermission, minRole, exactRole}
 * @param {object} who   { hasPermission: (perm) => boolean, role: string }
 */
export function canAccess(gate, who) {
  if (!gate) return true;                       // an ungated route is open to any session
  const hasPermission = who?.hasPermission || (() => false);
  const role = who?.role || 'Viewer';

  if (gate.requiredPermission && !hasPermission(gate.requiredPermission)) return false;
  if (Array.isArray(gate.anyPermission) && gate.anyPermission.length > 0
      && !gate.anyPermission.some((p) => hasPermission(p))) return false;
  if (gate.minRole && roleLevel(role) < gateLevel(gate.minRole)) return false;
  /* Strict equality, not a level compare — pins a surface to exactly one role
     and stays correct if a tier is ever added above it. */
  if (gate.exactRole && role !== gate.exactRole) return false;
  return true;
}

/** nav id → the guard its route carries in App.jsx. `null` = ungated. */
export const ROUTE_GATES = {
  overview:                null,
  agents:                  { requiredPermission: 'agent.read' },
  canvas:                  { requiredPermission: 'workflow.read', minRole: 'Developer' },
  identity:                { minRole: 'Tenant Admin' },
  'intent-profiles':       { anyPermission: ['policy.read', 'compliance.admin', 'policy.admin'] },
  approvals:               null,
  'my-tasks':              null,
  forms:                   { requiredPermission: 'workflow.read', minRole: 'Developer' },
  'shadow-discovery':      { minRole: 'Tenant Admin' },
  'workflow-discovery':    { minRole: 'Tenant Admin' },
  'protection-hub':        { minRole: 'Tenant Admin' },
  accountability:          { requiredPermission: 'audit.read' },
  'policy-config':         { anyPermission: ['policy.read', 'policy.admin', 'compliance.admin'] },
  skills:                  { requiredPermission: 'skill.read', minRole: 'Developer' },
  'native-trust':          { minRole: 'Tenant Admin' },
  compliance:              { anyPermission: ['policy.read', 'compliance.admin', 'audit.read'] },
  'workflow-audit':        { anyPermission: ['audit.read', 'gateway.audit.read', 'compliance.admin'] },
  'workflow-intelligence': { minRole: 'Tenant Admin' },
  'gel-agents':            { requiredPermission: 'agent.read' },
  'gel-connectors':        { minRole: 'Tenant Admin' },
  connectors:              { requiredPermission: 'connector.create', minRole: 'Developer' },
  'agent-marketplace':     { requiredPermission: 'agent.read', minRole: 'Developer' },
  'agentic-app-packs':     { requiredPermission: 'pack.read', minRole: 'Developer' },
  templates:               { minRole: 'Tenant Admin' },
  'admin-center':          { minRole: 'Tenant Admin' },
  team:                    { requiredPermission: 'user.manage', minRole: 'Tenant Admin' },
  secrets:                 { requiredPermission: 'secrets.read', minRole: 'Tenant Admin' },
  'multi-tenancy':         { requiredPermission: 'instance.read', minRole: 'Viewer' },
  customers:               { requiredPermission: 'identity.read', minRole: 'Platform Admin' },
  'operator-console':      { requiredPermission: 'workflow.execute', minRole: 'Operator' },
  'cloud-operations':      { minRole: 'Super Admin' },
  'gateway-keys':          { minRole: 'Tenant Admin' },
  'gateway-dashboard':     { minRole: 'Tenant Admin' },
  intelligence:            { minRole: 'Super Admin' },
  flags:                   { exactRole: 'Platform Admin' },
};

/** May this session see this nav id? Unknown ids are hidden, never shown. */
export function navItemVisible(id, who) {
  if (!Object.prototype.hasOwnProperty.call(ROUTE_GATES, id)) return false;
  return canAccess(ROUTE_GATES[id], who);
}
